
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowUp } from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { format, parseISO } from "date-fns";

interface EfficiencyChartProps {
  title?: string;
  data: Array<{ 
    date: string; 
    displayDate: string;
    value: number; 
    address?: string;
    rank?: number;
  }>;
  className?: string;
  tooltipContent?: string;
}

export function EfficiencyChart({ title = "Node Efficiency", data, className, tooltipContent }: EfficiencyChartProps) {
  if (!data.length) return null;

  const formatRank = (value: number) => {
    return `#${value}`;
  };

  // Ensure we have valid numeric values
  const validData = data.map(item => ({
    ...item,
    rank: typeof item.rank === 'number' ? item.rank : null,
  }));

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center gap-2">
              <span>{title}</span>
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger>
                    <InfoIcon className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{tooltipContent || "Node efficiency score and ranking over the last 24 hours"}</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-4">
                <span className="flex items-center text-purple-500">
                  <ArrowUp className="h-4 w-4 mr-1" />
                  Node Ranking
                </span>
              </div>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={validData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <XAxis 
                dataKey="displayDate" 
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                height={30}
                tickMargin={5}
                scale="point"
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatRank}
                width={40}
                reversed={true}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--background))",
                  border: "1px solid hsl(var(--border))",
                }}
                labelStyle={{ color: "hsl(var(--foreground))" }}
                formatter={(value: number) => [`#${value}`, "Node Ranking"]}
                labelFormatter={(label) => `Time: ${label}`}
              />
              <Line
                type="monotone"
                dataKey="rank"
                stroke="#9333ea"
                strokeWidth={2}
                dot={false}
                name="Node Ranking"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
